/**
 * In-app help for both portals: the "How this works" side panel (a full-screen sheet on a phone), the Help
 * section (#/help and #/help/<page>: searchable guides, the staff portal's reference documents and problem
 * reports), "Something's not working", the What's new card on the home screen, and the problem reports staff
 * answer.
 *
 * Each app is built with its own pages only (staff or customer), filtered here by the login's role. Wording comes
 * from the client layer.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent } from "react";
import { renderHelpMarkdown } from "@ops/client-config/markdown";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { FilterBar, PageHeader, SearchField } from "../components/PageHeader";
import { Chip, EmptyState, ErrorState, SkeletonLines } from "../components/States";
import { fmtDate, fmtPlainDate } from "../lib/format";
import { hashParams, useHash } from "../lib/route";
import { useAsync } from "../lib/useAsync";
import { openBlobInNewTab } from "../lib/jobs-data";
import {
  canCaptureScreen,
  captureScreen,
  documentFile,
  HELP,
  helpPage,
  helpPages,
  isCrewViewer,
  isManager,
  isStaffDirector,
  listDocuments,
  listMyReports,
  listReports,
  markWhatsNewSeen,
  openScreenshot,
  relatedPages,
  removeDocument,
  replyToReport,
  REPORT_STATUS_LABEL,
  reportsOn,
  searchPages,
  reportSettings,
  sendReport,
  unseenWhatsNew,
  uploadDocument,
  type DocumentAudience,
  type ReportStatus,
  type SupportReport,
} from "./data";
import { listeners, openReport, type PanelState } from "./links";

const homeHref = HELP.app === "staff" ? "#/today" : "#/";

/* ------------------------------------------------------------------ the panel */

function PageBody({ body, onLink }: { body: string; onLink?: (id: string) => void }) {
  const html = useMemo(() => renderHelpMarkdown(body), [body]);
  const click = (e: MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest("a");
    const href = a?.getAttribute("href") ?? "";
    if (onLink && href.startsWith("#/help/")) {
      e.preventDefault();
      onLink(href.slice(7));
    }
  };
  // renderHelpMarkdown escapes everything it does not produce itself.
  return <div className="helpdoc" onClick={click} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Mounted once by each app shell: the help panel and the report form. */
export function HelpHost() {
  const [state, setState] = useState<PanelState>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const l = (s: PanelState) => {
      if (s && !returnFocus.current) returnFocus.current = document.activeElement as HTMLElement | null;
      setState(s);
    };
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  useEffect(() => {
    if (!state) {
      returnFocus.current?.focus?.();
      returnFocus.current = null;
      return;
    }
    panelRef.current?.querySelector<HTMLElement>("h2")?.focus();
    document.body.classList.add("no-scroll");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setState(null);
      else if (e.key === "Tab") {
        // Keep keyboard focus inside the open panel.
        const items = panelRef.current?.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), input, textarea, select, h2");
        if (!items?.length) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("no-scroll");
    };
  }, [state]);
  useEffect(() => {
    const close = () => setState(null);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, []);
  if (!state) return null;
  const page = state.kind === "page" ? helpPage(state.id) : undefined;
  const related = page ? relatedPages(page) : [];
  return (
    <>
      <div className="helpscrim" aria-hidden="true" onClick={() => setState(null)} />
      <div ref={panelRef} className="helppanel" role="dialog" aria-modal="true" aria-labelledby="helppanel-title">
        <div className="helppanel__head">
          <h2 id="helppanel-title" tabIndex={-1}>{state.kind === "report" ? "Something's not working" : (page?.title ?? "Help")}</h2>
          <button type="button" className="icon-btn" aria-label={state.kind === "report" ? "Close report" : "Close help"} onClick={() => setState(null)}><Icon name="close" size={22} /></button>
        </div>
        <div className="helppanel__body">
          {state.kind === "report" ? (
            <ReportForm onDone={() => setState(null)} />
          ) : page ? (
            <>
              <p className="helppanel__summary">{page.summary}</p>
              <PageBody body={page.body} onLink={(id) => setState({ kind: "page", id })} />
              {related.length > 0 && (
                <>
                  <h3 className="helppanel__sub">Related</h3>
                  <ul className="helplinks">
                    {related.map((p) => (
                      <li key={p.id}><button type="button" className="linkbtn" onClick={() => setState({ kind: "page", id: p.id })}>{p.title}</button></li>
                    ))}
                  </ul>
                </>
              )}
              <div className="form-actions helppanel__foot">
                <a className="btn btn--outline btn--sm" href={`#/help/${page.id}`}>Open in Help</a>
                {reportsOn() && <button type="button" className="btn btn--ink btn--sm" onClick={() => setState({ kind: "report" })}>Something's not working</button>}
              </div>
            </>
          ) : (
            <p>That guide is not available.</p>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ Something's not working */

function ReportForm({ onDone }: { onDone: () => void }) {
  const route = useRef(window.location.hash || homeHref);
  const [trying, setTrying] = useState("");
  const [happened, setHappened] = useState("");
  const [shot, setShot] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [lostShot, setLostShot] = useState(false);
  const [canShot, setCanShot] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  useEffect(() => {
    let live = true;
    void reportSettings().then((s) => live && setCanShot(s.screenshots));
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    if (!shot) return setPreview(null);
    const u = URL.createObjectURL(shot);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [shot]);

  async function take() {
    setNotice(null);
    const b = await captureScreen();
    if (b) setShot(b);
    else setNotice({ tone: "error", text: "No screenshot was taken. You can attach a picture instead." });
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (trying.trim().length < 3) return setNotice({ tone: "error", text: "Say what you were trying to do." });
    setBusy(true);
    setNotice(null);
    try {
      const r = await sendReport({ trying: trying.trim(), happened: happened.trim(), route: route.current, screenshot: canShot ? shot : null });
      setLostShot(Boolean(shot && canShot && r.screenshotKept === false));
      setDone(r.message);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }
  if (done) {
    return (
      <div className="stack helpdone">
        <p className="notice notice--ok" role="status"><Icon name="check" size={18} />{done}</p>
        {lostShot && <p className="small">Your screenshot couldn't be kept, so describe what you saw in a reply if it helps.</p>}
        <p className="small muted">We have emailed you a copy. Any reply shows under Your reports in Help.</p>
        <div className="form-actions">
          <button type="button" className="btn btn--ink btn--sm" onClick={onDone}>Close</button>
          <a className="btn btn--outline btn--sm" href="#/help">Your reports</a>
        </div>
      </div>
    );
  }
  return (
    <form className="form" onSubmit={(e) => void submit(e)} noValidate>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <label className="field">
        <span className="field__label">What were you trying to do?</span>
        <textarea className="input" rows={3} required minLength={3} maxLength={4000} value={trying} onChange={(e) => setTrying(e.target.value)} />
      </label>
      <label className="field">
        <span className="field__label">What happened? (optional)</span>
        <textarea className="input" rows={3} maxLength={4000} value={happened} onChange={(e) => setHappened(e.target.value)} />
      </label>
      {canShot && (
      <div className="field">
        <span className="field__label">Screenshot (optional)</span>
        <div className="form-actions">
          {canCaptureScreen() && <button type="button" className="btn btn--outline btn--sm" onClick={() => void take()}>Take a screenshot</button>}
          <label className="btn btn--outline btn--sm helpfile">
            {canCaptureScreen() ? "Attach a picture" : "Add a screenshot or photo"}
            <input className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setShot(e.target.files?.[0] ?? null)} />
          </label>
        </div>
        {preview && (
          <div className="helpshot">
            <img src={preview} alt="The screenshot that will be sent" />
            <button type="button" className="linkbtn" onClick={() => setShot(null)}>Remove</button>
          </div>
        )}
      </div>
      )}
      <p className="field__hint">Your name, your role, this screen, the app version, your browser, the time and any recent errors are added for you.</p>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy} aria-busy={busy}>Send report</button>
        <button type="button" className="btn btn--outline btn--sm" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

/** "Something's not working" for the user menu and the Help section. */
export function ReportButton({ className = "btn btn--outline btn--sm", labelClass, title }: { className?: string; labelClass?: string; title?: string }) {
  if (!reportsOn()) return null;
  return (
    <button type="button" className={className} onClick={openReport} title={title}>
      <Icon name="warning" size={20} />
      <span className={labelClass}>Something's not working</span>
    </button>
  );
}

/* ------------------------------------------------------------------ the Help section */

export function HelpScreen() {
  const [id] = hashParams(useHash());
  if (id) return <HelpPageView key={id} id={id} />;
  return <HelpHome />;
}

function HelpHome() {
  const [q, setQ] = useState("");
  const pages = searchPages(q);
  const total = helpPages().length;
  return (
    <section className="page stack" aria-labelledby="page-title">
      <PageHeader help={false} eyebrow="Help" title="Help" meta="Guides for each screen, and a way to tell us when something is not working." actions={<ReportButton />} />
      <FilterBar count={q ? `${pages.length} ${pages.length === 1 ? "guide" : "guides"}` : undefined}>
        <SearchField label="Search help" placeholder="Search the guides" value={q} onChange={setQ} />
      </FilterBar>
      {!pages.length ? (
        <EmptyState help={false} icon="inbox" title={total ? "No guides match" : "No guides yet"}>{total ? "Try other words." : "Guides appear here when they are added."}</EmptyState>
      ) : (
        <div className="card">
          <ul className="list">
            {pages.map((p) => (
              <li key={p.id} className="list__row">
                <span><a className="strong" href={`#/help/${p.id}`}>{p.title}</a><span className="sub">{p.summary}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {HELP.app === "staff" && <Documents />}
      {isManager() ? <AllReports /> : <MyReports />}
    </section>
  );
}

/* ------------------------------------------------------------------ reference documents (staff portal) */

const AUDIENCE_LABEL: Record<DocumentAudience, string> = { office: "Office", crew: "Site", all: "Everyone" };

/** Guides, workbooks and templates a director uploads, for the office, site staff or everyone. */
function Documents() {
  const s = useAsync(listDocuments);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const docs = s.data ?? [];
  if (!isStaffDirector() && !s.loading && !docs.length) return null;
  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const file = f.get("file");
    if (!(file instanceof File) || !file.size) return;
    setBusy(true);
    setNotice(null);
    try {
      await uploadDocument(String(f.get("title") ?? ""), String(f.get("audience") ?? "all") as DocumentAudience, file);
      ok("Document added.");
      setAdding(false);
      s.reload();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="card">
      <div className="card__head">
        <h2 className="card__title">Reference documents</h2>
        {isStaffDirector() && !adding && <button type="button" className="btn btn--outline btn--sm" onClick={() => setAdding(true)}><Icon name="plus" size={16} /> Add document</button>}
      </div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        {s.loading && !s.data && <SkeletonLines rows={2} />}
        {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
        {adding && (
          <form className="form-grid" onSubmit={(e) => void add(e)}>
            <label className="field"><span className="field__label">Title</span><input className="input" name="title" required maxLength={200} placeholder="Office guide" /></label>
            <label className="field"><span className="field__label">Who sees it</span>
            {/* vocab-ok: the field-services document audience (help_documents.audience) */}
              <select className="input" name="audience" defaultValue="all"><option value="all">Everyone</option><option value="office">Office only</option><option value="crew">Site staff</option></select>
            </label>
            <label className="field field--wide"><span className="field__label">File</span><input className="input" name="file" type="file" required accept=".pdf,.docx,.xlsx,image/png,image/jpeg" /></label>
            <div className="form-actions field--wide">
              <button type="submit" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy}>Upload</button>
              <button type="button" className="btn btn--outline btn--sm" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </form>
        )}
        {s.data && !docs.length && !adding && <p className="small muted">No documents yet. Add guides, workbooks and templates people need to hand.</p>}
        {docs.length > 0 && (
          <ul className="list">
            {docs.map((d) => (
              <li key={d.id} className="list__row">
                <span>
                  <button type="button" className="linkbtn strong" onClick={() => void openBlobInNewTab(() => documentFile(d.id)).catch(fail)}>{d.title}</button>
                  <span className="sub">{d.file_name}{isCrewViewer() ? "" : `, ${AUDIENCE_LABEL[d.audience]}`}</span>
                </span>
                {isStaffDirector() && (
                  <button type="button" className="btn btn--outline btn--xs" onClick={() => void removeDocument(d.id).then(() => { ok("Document removed."); s.reload(); }).catch(fail)}>Remove</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function HelpPageView({ id }: { id: string }) {
  const page = helpPage(id);
  if (!page) {
    return (
      <section className="page stack" aria-labelledby="page-title">
        <PageHeader help={false} eyebrow="Help" title="Guide not found" />
        <p><a href="#/help">All guides</a></p>
      </section>
    );
  }
  const related = relatedPages(page);
  const screen = page.route.startsWith("#/") && page.route !== "#/help" && page.route !== "#/milestones" ? page.route : null;
  return (
    <section className="page stack" aria-labelledby="page-title">
      <a href="#/help" className="helpback"><Icon name="collapse" size={18} /> All guides</a>
      <PageHeader help={false} eyebrow="Help" title={page.title} meta={page.summary} actions={screen ? <a className="btn btn--outline btn--sm" href={screen}>Go to the screen</a> : undefined} />
      <div className="card"><div className="card__body"><PageBody body={page.body} onLink={(p) => (window.location.hash = `#/help/${p}`)} /></div></div>
      <p className="small muted">Updated {fmtPlainDate(page.updated)}{page.platform ? `, for ${page.platform}` : ""}.</p>
      {related.length > 0 && (
        <div className="card">
          <div className="card__head"><h2 className="card__title">Related</h2></div>
          <ul className="list">{related.map((p) => <li key={p.id} className="list__row"><span><a className="strong" href={`#/help/${p.id}`}>{p.title}</a><span className="sub">{p.summary}</span></span></li>)}</ul>
        </div>
      )}
      {reportsOn() && <div className="form-actions"><ReportButton /></div>}
    </section>
  );
}

/* ------------------------------------------------------------------ problem reports */

const STATUS_TONE = { open: "accent", answered: "grey", fixed: "green" } as const;

function MyReports() {
  const s = useAsync(listMyReports);
  const reports = s.data ?? [];
  if (!reportsOn() || (!reports.length && !s.error)) return null;
  return (
    <section className="card" aria-labelledby="myreports-title">
      <div className="card__head"><h2 className="card__title" id="myreports-title">Your reports</h2></div>
      <div className="card__body stack">
        {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
        {reports.map((r) => <ReportRow key={r.id} r={r} onChange={s.reload} />)}
      </div>
    </section>
  );
}

/** Staff directors and admins: every report, staff and customer. */
function AllReports() {
  const s = useAsync(() => listReports());
  const [show, setShow] = useState<"open" | "all">("open");
  const all = s.data ?? [];
  const reports = show === "open" ? all.filter((r) => r.status === "open") : all;
  return (
    <section className="card" aria-labelledby="reports-title">
      <div className="card__head">
        <h2 className="card__title" id="reports-title">Problem reports</h2>
        <div className="seg" role="group" aria-label="Which reports">
          <button type="button" className="seg__btn" aria-pressed={show === "open"} onClick={() => setShow("open")}>Open</button>
          <button type="button" className="seg__btn" aria-pressed={show === "all"} onClick={() => setShow("all")}>All</button>
        </div>
      </div>
      <div className="card__body stack">
        {s.loading && !s.data && <SkeletonLines rows={2} />}
        {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
        {s.data && !reports.length && <p className="small muted">{show === "open" ? "No open reports." : "No reports yet."} Reports from staff and customers appear here.</p>}
        {reports.map((r) => <ReportRow key={r.id} r={r} onChange={s.reload} />)}
      </div>
    </section>
  );
}

/** A customer's reports on their page in Customers, next to their feedback. */
export function CustomerReports({ customerId }: { customerId: string }) {
  const s = useAsync(() => listReports(customerId), [customerId]);
  const reports = s.data ?? [];
  if (!reports.length && !s.error) return null;
  const open = reports.filter((r) => r.status === "open").length;
  return (
    <section className={`card${open ? " card--alert" : ""}`} aria-labelledby="custreports-title">
      <div className="card__head">
        <h2 className="card__title" id="custreports-title">Problem reports</h2>
        <span className="muted small">{open} open</span>
      </div>
      <div className="card__body stack">
        {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
        {reports.map((r) => <ReportRow key={r.id} r={r} onChange={s.reload} onCustomerPage />)}
      </div>
    </section>
  );
}

const ROLE_LABEL: Record<string, string> = { director: "Director", admin: "Admin", controller: "Controller", owner: "Owner", member: "Member", accounts: "Accounts" };

function ReportRow({ r, onChange, onCustomerPage }: { r: SupportReport; onChange: () => void; onCustomerPage?: boolean }) {
  const manager = isManager();
  const [status, setStatus] = useState<ReportStatus>(r.status);
  const [reply, setReply] = useState(r.reply ?? "");
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const who = `${r.reporter_name ?? r.reporter_email ?? "Someone"} (${ROLE_LABEL[r.role] ?? r.role}${r.app === "customer" ? ", customer portal" : ""})`;
  return (
    <div className="helpreport">
      <div className="helpreport__head">
        <p className="strong">{r.trying}</p>
        <Chip tone={STATUS_TONE[r.status]}>{REPORT_STATUS_LABEL[r.status]}</Chip>
      </div>
      <p className="small muted">
        {fmtDate(r.created_at)}
        {manager ? `, ${who}${r.route ? ` on ${r.route}` : ""}${r.platform_version ? `, ${r.platform_version}` : ""}` : ""}
        {manager && !onCustomerPage && r.customer_id && <>{". "}<a href={`#/customers/${r.customer_id}`}>Open the customer</a></>}
      </p>
      {r.happened && <p className="small helpreport__text">{r.happened}</p>}
      {r.screenshot_key && <button type="button" className="linkbtn small" onClick={() => void openScreenshot(r.id).catch(fail)}>View screenshot</button>}
      {manager && r.api_errors.length > 0 && <ul className="small muted helpreport__errors">{r.api_errors.map((e, i) => <li key={i}>{e.status} {e.path}: {e.message}</li>)}</ul>}
      {r.reply && !manager && (
        <div className="helpreply" role="status">
          <span className="field__label">Our reply</span>
          <p>{r.reply}</p>
          {r.replied_at && <span className="sub">{r.replied_by_name ? `${r.replied_by_name}, ` : ""}{fmtDate(r.replied_at)}</span>}
        </div>
      )}
      {manager && (
        <form
          className="form-grid helpreport__form"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            setNotice(null);
            replyToReport(r.id, status, reply.trim() || null)
              .then(() => {
                ok("Saved. The person who reported it sees the reply in Help.");
                onChange();
              })
              .catch(fail)
              .finally(() => setBusy(false));
          }}
        >
          <div className="field--wide"><Notice notice={notice} onDismiss={() => setNotice(null)} /></div>
          <label className="field field--wide"><span className="field__label">Reply</span><textarea className="input" rows={2} maxLength={4000} value={reply} onChange={(e) => setReply(e.target.value)} /></label>
          <label className="field"><span className="field__label">Status</span>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value as ReportStatus)}>
              <option value="open">Open</option><option value="answered">Answered</option><option value="fixed">Fixed</option>
            </select>
          </label>
          <div className="form-actions helpreport__save">
            <button type="submit" className="btn btn--ink btn--xs" disabled={busy} aria-busy={busy}>Save</button>
            {r.replied_at && <span className="sub">Replied {r.replied_by_name ? `by ${r.replied_by_name}, ` : ""}{fmtDate(r.replied_at)}</span>}
          </div>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ What's new on the home screen */

/** Shown on the first visit after a new entry; dismissing it records the newest entry as seen for this login. */
export function WhatsNewCard() {
  const s = useAsync(unseenWhatsNew);
  const [hidden, setHidden] = useState(false);
  const entries = s.data ?? [];
  if (hidden || !entries.length) return null;
  const newest = entries[0]!;
  const dismiss = () => {
    setHidden(true);
    void markWhatsNewSeen().catch(() => undefined);
  };
  return (
    <section className="card whatsnew" aria-labelledby="whatsnew-title">
      <div className="card__head">
        <h2 className="card__title" id="whatsnew-title">What's new: {newest.title}</h2>
        <button type="button" className="icon-btn" aria-label="Dismiss What's new" onClick={dismiss}><Icon name="close" size={20} /></button>
      </div>
      <div className="card__body">
        <PageBody body={newest.body} />
        <div className="form-actions">
          <button type="button" className="btn btn--ink btn--sm" onClick={dismiss}>Got it</button>
          {entries.length > 1 && helpPage("whats-new") && <a className="btn btn--outline btn--sm" href="#/help/whats-new" onClick={dismiss}>{entries.length - 1} earlier {entries.length === 2 ? "update" : "updates"}</a>}
        </div>
      </div>
    </section>
  );
}
